import { useCallback, useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, Copy, Eraser, Wand2, Command, CornerDownLeft, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, isPaywall } from "@/lib/api";
import { ShareButtons } from "@/components/ShareButtons";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import { Waveform } from "@/components/Waveform";
import { copyText } from "@/components/FloatingMic";
import { useDictation } from "@/lib/useDictation";
import { TONES, MODELS, toneLabel } from "@/lib/constants";
import { LanguagePicker } from "@/components/LanguagePicker";
import { langLabel } from "@/lib/languages";
import { QuickActions, PasteButton, SHARED_KEY } from "@/components/SharedText";

const isTyping = (el) => el && (["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) || el.isContentEditable);

const Pills = ({ items, value, onChange, testPrefix }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map((i) => (
      <button key={i.id} onClick={() => onChange(i.id)} data-testid={`${testPrefix}-${i.id}`}
        className={`pill ${value === i.id ? "pill-on" : ""}`}>{i.label}</button>
    ))}
  </div>
);

const Select = ({ value, onChange, items, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="select">
    {items.map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
  </select>
);

const Caption = ({ text, testId }) => (
  <p className="mt-3 min-h-[1.5rem] text-sm text-white/80 italic" data-testid={testId}>
    {text ? <>{text.slice(-220)}<span className="inline-block w-1.5 h-4 ml-1 align-middle bg-[var(--accent)] animate-pulse" /></> : null}
  </p>
);

const TOUCH = window.matchMedia?.("(pointer: coarse)").matches;

function MicStage({ dict, mode }) {
  const { status, levels, elapsed, caption, start, stop, toggle } = dict;
  const rec = status === "recording";
  const busy = status === "processing";
  const handlers = mode === "hold"
    ? {
      onPointerDown: (e) => { e.preventDefault(); e.currentTarget.setPointerCapture?.(e.pointerId); start(); },
      onPointerUp: stop, onPointerCancel: stop, onContextMenu: (e) => e.preventDefault(),
    }
    : { onClick: toggle, onContextMenu: (e) => e.preventDefault() };
  const hint = busy ? "Polishing your words…" : rec ? (mode === "hold" ? "Listening — release to finish" : "Listening — tap to finish")
    : mode === "hold" ? (TOUCH ? "Hold the button and speak" : "Hold the button or the spacebar and speak") : (TOUCH ? "Tap the button to start" : "Tap the button or press space to start");
  return (
    <div className="panel p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6 sm:gap-8" data-testid="mic-stage">
      <button {...handlers} disabled={busy} data-testid="main-mic-button" aria-label="Dictate"
        className={`mic-orb ${rec ? "mic-orb-live" : ""} select-none touch-none`}>
        {busy ? <Loader2 size={30} className="animate-spin" /> : rec ? <Square size={26} fill="currentColor" /> : <Mic size={32} />}
      </button>
      <div className="flex-1 w-full min-w-0">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-white/70" data-testid="mic-status-text">{hint}</span>
          <span className="text-xs tabular-nums text-white/40" data-testid="mic-timer">{rec ? `${elapsed.toFixed(1)}s` : "0.0s"}</span>
        </div>
        <Waveform levels={levels} active={rec} className="h-14" testId="main-waveform" />
        {(rec || busy) && <Caption text={caption} testId="live-caption" />}
      </div>
    </div>
  );
}

function CommandBar({ text, setText, editorRef, opts }) {
  const [typed, setTyped] = useState("");
  const [running, setRunning] = useState(false);
  const sel = useRef({ s: 0, e: 0 });
  const textRef = useRef(text);
  textRef.current = text;
  const grab = () => {
    const el = editorRef.current;
    const s = el?.selectionStart ?? 0, e = el?.selectionEnd ?? 0;
    sel.current = s !== e ? { s, e, part: true } : { s: 0, e: textRef.current.length, part: false };
  };
  const apply = async (fd) => {
    const { s, e } = sel.current;
    fd.append("text", textRef.current.slice(s, e));
    if (opts.language) fd.append("language", opts.language);
    if (opts.model) fd.append("model", opts.model);
    const { data } = await api.post("/dictation/command", fd);
    setText((t) => t.slice(0, s) + data.text + t.slice(e));
    toast.success(`Done: "${data.instruction}"`);
  };
  const dict = useDictation({
    getOptions: () => opts,
    upload: async (blob, name) => { const fd = new FormData(); fd.append("audio", blob, name); await apply(fd); },
  });
  const rec = dict.status === "recording";
  const busy = dict.status === "processing" || running;
  const submitTyped = async (ev) => {
    ev.preventDefault();
    if (!typed.trim()) return;
    grab();
    setRunning(true);
    try { const fd = new FormData(); fd.append("instruction", typed.trim()); await apply(fd); setTyped(""); }
    catch (e) { if (!isPaywall(e)) toast.error(apiError(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };
  return (
    <div className="border-t border-white/[0.06] px-4 py-3" data-testid="command-bar">
      <form onSubmit={submitTyped} className="flex items-center gap-2">
        <button type="button" disabled={!text || busy} data-testid="command-mic-button" aria-label="Hold and say a command"
          onPointerDown={(e) => { e.preventDefault(); e.currentTarget.setPointerCapture?.(e.pointerId); grab(); dict.start(); }}
          onPointerUp={dict.stop} onPointerCancel={dict.stop} onContextMenu={(e) => e.preventDefault()}
          className={`pill shrink-0 select-none touch-none ${rec ? "!bg-[var(--accent)] !text-black !border-[var(--accent)]" : ""}`}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : rec ? <Square size={11} fill="currentColor" /> : <Command size={13} />}
          {rec ? "Listening…" : "Hold & command"}
        </button>
        <input type="text" autoComplete="off" autoCapitalize="sentences" autoCorrect="on" spellCheck enterKeyHint="go" aria-label="Type a command" value={typed} onChange={(e) => setTyped(e.target.value)} disabled={!text || busy} data-testid="command-input"
          placeholder='Select text, then say or type "make this shorter", "translate to Hindi"…'
          className="flex-1 min-w-0 bg-transparent text-sm text-[var(--ink)] placeholder:text-white/30 outline-none" />
        <button type="submit" disabled={!text || busy || !typed.trim()} data-testid="command-submit-button" className="pill shrink-0" aria-label="Run command"><CornerDownLeft size={13} /></button>
      </form>
      {rec && <Caption text={dict.caption} testId="command-caption" />}
    </div>
  );
}

function Editor({ text, setText, opts, onRewrite, rewriting, shared }) {
  const editorRef = useRef(null);
  return (
    <div className="panel p-0 overflow-hidden" data-testid="editor-panel">
      <textarea ref={editorRef} autoCapitalize="sentences" autoCorrect="on" spellCheck aria-label="Your text" value={text} onChange={(e) => setText(e.target.value)} data-testid="flow-editor"
        placeholder="Your polished words land here. Edit freely, then copy into any app."
        className="w-full min-h-[260px] bg-transparent p-6 text-base leading-relaxed text-[var(--ink)] placeholder:text-white/25 outline-none resize-y" />
      <div className="flex flex-wrap items-center gap-2 border-t border-white/[0.06] px-4 py-3">
        <button onClick={() => copyText(text)} disabled={!text} data-testid="editor-copy-button" className="btn-accent text-xs"><Copy size={14} /> Copy</button>
        <button onClick={() => setText("")} disabled={!text} data-testid="editor-clear-button" className="btn-ghost text-xs"><Eraser size={14} /> Clear</button>
        <PasteButton onPaste={(t) => { setText(t); onRewrite("auto", t); }} />
        <span className="mx-1 h-5 w-px bg-white/10 hidden sm:block" />
        <span className="text-xs text-white/40 flex items-center gap-1"><Wand2 size={13} /> Rewrite as</span>
        {TONES.filter((t) => t.id !== "verbatim").map((t) => (
          <button key={t.id} disabled={!text || rewriting} onClick={() => onRewrite(t.id)} data-testid={`rewrite-${t.id}`}
            className="pill text-xs">{rewriting === t.id ? <Loader2 size={12} className="animate-spin" /> : t.label}</button>
        ))}
        <span className="ml-auto text-xs text-white/35" data-testid="editor-word-count">{text.trim() ? text.trim().split(/\s+/).length : 0} words · {MODELS.find((m) => m.id === opts.model)?.label}</span>
      </div>
      <QuickActions text={text} setText={setText} opts={opts} shared={shared} />
      <div className="flex items-center gap-3 border-t border-white/[0.06] px-4 py-2.5">
        <span className="text-xs text-white/40">Share</span>
        <ShareButtons text={text} testPrefix="editor-share" compact />
      </div>
      <CommandBar text={text} setText={setText} editorRef={editorRef} opts={opts} />
    </div>
  );
}

function OriginalPanel({ original, onClose }) {
  return (
    <div className="panel p-5" data-testid="original-panel">
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="eyebrow" data-testid="original-panel-label">You said · translated to {langLabel(original.to)}</span>
        <div className="flex items-center gap-1">
          <button onClick={() => copyText(original.text)} data-testid="original-copy-button" className="pill text-xs"><Copy size={12} /> Copy</button>
          <button onClick={onClose} aria-label="Hide original" data-testid="original-close-button" className="p-1 text-white/40 hover:text-white transition-colors"><X size={15} /></button>
        </div>
      </div>
      <p className="text-sm text-white/60 italic whitespace-pre-wrap" data-testid="original-text">{original.text}</p>
    </div>
  );
}

function Recent({ items, onUse }) {
  return (
    <div data-testid="recent-list">
      <div className="eyebrow mb-4">This session</div>
      {items.length === 0 && <p className="text-sm text-white/35">Nothing yet. Your first dictation will appear here.</p>}
      <div className="space-y-3">
        {items.map((d) => (
          <button key={d.id} onClick={() => onUse(d.text)} data-testid={`recent-item-${d.id}`}
            className="w-full text-left panel p-4 hover:border-[var(--accent)]/40 transition-colors">
            <div className="flex justify-between text-[11px] text-white/35 mb-1.5"><span>{toneLabel(d.tone)}</span><span>{d.words} words</span></div>
            <p className="text-sm text-white/75 line-clamp-3">{d.text}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Flow() {
  const [text, setText] = useState("");
  const [mode, setMode] = useState("hold");
  const [opts, setOpts] = useState({ tone: "auto", language: "auto", model: "sonnet", live_captions: "browser", output_language: "auto" });
  const [original, setOriginal] = useState(null);
  const [recent, setRecent] = useState([]);
  const [rewriting, setRewriting] = useState(null);
  const [shared, setShared] = useState(false);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => { api.get("/settings").then(({ data }) => setOpts((o) => ({ ...o, ...data }))).catch(() => {}); }, []);
  useEffect(() => {
    const s = sessionStorage.getItem(SHARED_KEY);
    if (!s) return;
    sessionStorage.removeItem(SHARED_KEY);
    setText(s);
    setShared(true);
    toast.success("Shared text is ready — polish, translate or reply below.");
  }, []);

  const onResult = useCallback((d) => {
    setText((t) => (t.trim() ? `${t.trimEnd()}\n\n${d.text}` : d.text));
    setRecent((r) => [d, ...r].slice(0, 6));
    setOriginal(d.translated_to ? { text: d.raw_text, to: d.translated_to } : null);
  }, []);
  const dict = useDictation({ onResult, getOptions: () => optsRef.current });
  const { start, stop, toggle } = dict;

  useEffect(() => {
    const down = (e) => {
      if (e.code !== "Space" || e.repeat || isTyping(e.target) || e.ctrlKey || e.metaKey || document.querySelector("[data-testid=voice-keyboard]")) return;
      e.preventDefault();
      mode === "hold" ? start() : toggle();
    };
    const up = (e) => {
      if (e.code !== "Space" || isTyping(e.target) || mode !== "hold" || document.querySelector("[data-testid=voice-keyboard]")) return;
      e.preventDefault();
      stop();
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, [mode, start, stop, toggle]);

  const set = (k) => (v) => setOpts((o) => ({ ...o, [k]: v }));

  const rewrite = async (tone, src = text) => {
    setRewriting(tone);
    try {
      const { data } = await api.post("/dictation/rewrite", { text: src, tone, language: opts.output_language && opts.output_language !== "auto" ? opts.output_language : opts.language, model: opts.model });
      setText(data.text);
      toast.success(`Rewritten as ${toneLabel(tone)}`);
    } catch (e) {
      if (!isPaywall(e)) toast.error(apiError(e.response?.data?.detail));
    } finally {
      setRewriting(null);
    }
  };

  return (
    <AppShell>
      <div className="max-w-6xl" data-testid="flow-page">
        <PageHeader eyebrow="Workspace" title={<>Speak naturally.<br /><em className="text-[var(--accent)]">Mulankan</em> writes it right.</>}
          sub="Talk the way you think — in Hindi, English, Hinglish or 100+ other languages, and even translate as you speak. Filler words vanish, grammar gets fixed, and your tone stays yours." />
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-6 sm:gap-8">
          <div className="space-y-5 fade-up" style={{ animationDelay: "80ms" }}>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <div className="flex rounded-full bg-white/[0.04] p-1" data-testid="mode-toggle">
                {[["hold", "Hold to talk"], ["tap", "Tap to record"]].map(([id, l]) => (
                  <button key={id} onClick={() => setMode(id)} data-testid={`mode-${id}`}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${mode === id ? "bg-[var(--ink)] text-black" : "text-white/50"}`}>{l}</button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2 min-w-0 max-w-full" data-testid="translate-row">
                <LanguagePicker value={opts.language} onChange={set("language")} testId="language-select" />
                <ArrowRight size={14} className="text-white/35 shrink-0" />
                <LanguagePicker single value={opts.output_language} onChange={set("output_language")} testId="write-in-select"
                  autoLabel="Write as spoken" title="Write it in" hint="Speak in any language. Mulankan writes the polished text in the one you pick here." />
              </div>
              <Select value={opts.model} onChange={set("model")} items={MODELS} testId="model-select" />
            </div>
            <Pills items={TONES} value={opts.tone} onChange={set("tone")} testPrefix="tone" />
            <MicStage dict={dict} mode={mode} />
            <Editor text={text} setText={setText} opts={opts} onRewrite={rewrite} rewriting={rewriting} shared={shared} />
            {original && <OriginalPanel original={original} onClose={() => setOriginal(null)} />}
          </div>
          <div className="fade-up" style={{ animationDelay: "160ms" }}><Recent items={recent} onUse={copyText} /></div>
        </div>
      </div>
    </AppShell>
  );
}
